import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Trash2,
  Edit2,
  X,
  RefreshCw,
  AlertCircle,
  Layers,
  Percent,
  DollarSign,
  ShieldAlert,
} from "lucide-react";
import api from "../services/api"; // Centralized authenticated Axios instance

const UnifiedBuilderManager = () => {
  const [builders, setBuilders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // UI View States
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const initialFormState = {
    salesmanId: "",
    name: "",
    periodType: "monthly",
    payoutStructure: "progressive", // "progressive" vs "flat_percentage" vs "flat_amount"
    brackets: [{ from: 0, to: 1000000, rate: 5.0 }],
    isManager: false,
    managerTeamRate: 1.5,
  };
  const [formData, setFormData] = useState(initialFormState);

  // ─── API Transactions ──────────────────────────────────────────────────────
  const fetchBuilders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/builders");
      setBuilders(res.data.data || res.data);
    } catch (err) {
      const errorText = err.response?.data?.message || err.message;
      setError(`Could not pull registry data: ${errorText}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBuilders();
  }, [fetchBuilders]);

  // ─── Event Handlers ────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleStructureChange = (e) => {
    const type = e.target.value;
    setFormData((prev) => {
      let defaultBrackets = [...prev.brackets];

      if (type === "flat_percentage") {
        defaultBrackets = [
          {
            from: 0,
            to: 999999999,
            rate: prev.brackets[0]?.rate || 5.0,
            isFlatAmount: false,
          },
        ];
      } else if (type === "flat_amount") {
        defaultBrackets = [
          {
            from: 0,
            to: 999999999,
            rate: prev.brackets[0]?.rate || 500,
            isFlatAmount: true,
          },
        ];
      } else if (type === "progressive") {
        defaultBrackets = [
          { from: 0, to: 1000000, rate: 4.0, isFlatAmount: false },
          { from: 1000000, to: 999999999, rate: 6.0, isFlatAmount: false },
        ];
      }

      return { ...prev, payoutStructure: type, brackets: defaultBrackets };
    });
  };

  // ─── UNLOCKED MANUAL BRACKET MANIPULATION ENGINE ────────────────────────────
  const handleBracketChange = (index, field, val) => {
    const brackets = [...formData.brackets];

    // Accept raw text updates, fall back safely to 0 if input clears out entirely
    const numericValue = val === "" ? 0 : Number(val);
    brackets[index][field] = numericValue;

    setFormData((prev) => ({ ...prev, brackets }));
  };

  // Toggle infinity cap indicator explicitly per bracket row
  const toggleInfiniteCap = (index) => {
    const brackets = [...formData.brackets];
    if (brackets[index].to >= 999999999) {
      brackets[index].to = brackets[index].from + 1000000; // Reset to editable range
    } else {
      brackets[index].to = 999999999; // Explicit infinity horizon allocation
    }
    setFormData((prev) => ({ ...prev, brackets }));
  };

  const handleAddTier = () => {
    const brackets = [...formData.brackets];
    const last = brackets[brackets.length - 1];

    // Automatically pre-populate next baseline floor to make user data input flow fast
    const nextFrom = last ? last.to : 0;
    const nextTo = nextFrom >= 999999999 ? nextFrom + 1000000 : 999999999;

    brackets.push({
      from: nextFrom >= 999999999 ? 0 : nextFrom,
      to: nextTo,
      rate: 5.0,
      isFlatAmount: false,
    });
    setFormData((prev) => ({ ...prev, brackets }));
  };

  const handleRemoveTier = (index) => {
    if (formData.brackets.length === 1) return;
    const brackets = formData.brackets.filter((_, i) => i !== index);
    setFormData((prev) => ({ ...prev, brackets }));
  };

  // ─── CRUD Workflows ────────────────────────────────────────────────────────
  const startCreate = () => {
    setEditingId(null);
    setFormData(initialFormState);
    setShowForm(true);
  };

  const startEdit = (builder) => {
    const brackets = builder.brackets || [];
    let structure = "progressive";

    if (brackets.length === 1 && brackets[0].to >= 999999999) {
      structure = brackets[0].isFlatAmount ? "flat_amount" : "flat_percentage";
    }

    setEditingId(builder.salesmanId);
    setFormData({
      salesmanId: builder.salesmanId,
      name: builder.name,
      periodType: builder.periodType,
      payoutStructure: structure,
      brackets: JSON.parse(JSON.stringify(brackets)),
      isManager: !!builder.isManager,
      managerTeamRate:
        builder.managerTeamRate !== undefined ? builder.managerTeamRate : 1.5,
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Permanently delete this builder?")) return;
    try {
      await api.delete(`/builders/${id}`);
      fetchBuilders();
    } catch (err) {
      const errorText = err.response?.data?.message || err.message;
      setError(`Failed to delete record: ${errorText}`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    const { payoutStructure, ...cleanFormData } = formData;
    const payload = {
      ...cleanFormData,
      salesmanId: Number(formData.salesmanId),
      managerTeamRate: cleanFormData.isManager
        ? Number(cleanFormData.managerTeamRate)
        : null,
      type: "builder",
    };

    try {
      if (editingId) {
        await api.put(`/builders/${editingId}`, payload);
      } else {
        await api.post("/builders", payload);
      }

      setShowForm(false);
      fetchBuilders();
    } catch (err) {
      const errorText = err.response?.data?.message || err.message;
      setError(`Server rejected entry parameters: ${errorText}`);
    } finally {
      setSubmitting(false);
    }
  };

  // ─── Pure Accounting Formatting Functions ─────────────────────────
  const formatCurrency = (val, maxDigits = 0) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDigits,
    }).format(val || 0);
  };

  const formatPeriodLabel = (label) => {
    if (!label) return "";
    if (
      label === "semi_annual" ||
      label === "semi-annual" ||
      label === "semi_yearly"
    ) {
      return "Semi-Annual";
    }
    return label;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] w-full">
        <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-8 w-full space-y-6 bg-slate-50/50 text-slate-800 font-sans min-h-screen relative">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4 bg-white -mx-8 px-8 pt-2 shadow-sm">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
            Builders Commissions Settings
          </h1>
        </div>
        <button
          onClick={startCreate}
          className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-slate-800 transition shadow-sm"
        >
          <Plus className="w-4 h-4" /> Add Builder
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2 shadow-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      {/* ─── MODAL BOX OVERLAY CONTAINER ─── */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 bg-slate-900/40 backdrop-blur-sm">
          <form
            onSubmit={handleSubmit}
            className="relative bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto space-y-5 flex flex-col transform transition-all"
          >
            {/* Modal Header */}
            <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
              <h3 className="text-base font-bold text-slate-900 tracking-wide">
                {editingId
                  ? `Modify Configuration Model: ID ${editingId}`
                  : "Create New Commission Rule Mapping"}
              </h3>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form inputs container */}
            <div className="px-6 space-y-5 flex-1 overflow-y-auto pb-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-slate-600 font-semibold text-xs mb-1.5">
                    Salesman ID
                  </label>
                  <input
                    type="number"
                    name="salesmanId"
                    disabled={editingId !== null}
                    value={formData.salesmanId}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-800 font-mono disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-slate-200 transition"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold text-xs mb-1.5">
                    Builder Full Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 transition"
                    placeholder="e.g. Acme Corp"
                  />
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold text-xs mb-1.5">
                    Interval Cycle
                  </label>
                  <select
                    name="periodType"
                    value={formData.periodType}
                    onChange={handleInputChange}
                    className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 transition text-sm"
                  >
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="semi_annual">Semi-Annual</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div>
                  <label className="block text-slate-600 font-semibold text-xs mb-1.5">
                    Payout Architecture Type
                  </label>
                  <select
                    name="payoutStructure"
                    value={formData.payoutStructure}
                    onChange={handleStructureChange}
                    className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 font-bold text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
                  >
                    <option value="progressive">
                      Progressive Tier Brackets
                    </option>
                    <option value="flat_percentage">
                      Flat Percentage Rate (%)
                    </option>
                    <option value="flat_amount">
                      Flat Fixed Cash Amount ($)
                    </option>
                  </select>
                </div>
              </div>

              {/* MANAGER SETTINGS SECTION */}
              <div className="bg-blue-50/40 p-4 border border-blue-100 rounded-xl flex flex-wrap items-center justify-between gap-4">
                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    name="isManager"
                    checked={formData.isManager}
                    onChange={handleInputChange}
                    className="w-4 h-4 border-slate-300 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                  />
                  <div>
                    <span className="block font-bold text-slate-900 text-xs">
                      Assign as Sales Manager Role
                    </span>
                    <span className="block text-slate-500 text-[11px]">
                      Enables additional cumulative team-sales tracking
                      commission cycles.
                    </span>
                  </div>
                </label>

                {formData.isManager && (
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
                      Additional Team Rate:
                    </span>
                    <div className="relative rounded-lg shadow-sm w-24">
                      <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center text-slate-400 pointer-events-none">
                        <Percent className="w-3.5 h-3.5" />
                      </div>
                      <input
                        type="number"
                        name="managerTeamRate"
                        step="0.01"
                        value={formData.managerTeamRate}
                        onChange={handleInputChange}
                        required
                        className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-2 py-1 text-right font-mono font-bold text-slate-900 focus:outline-none text-xs"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* DYNAMIC RULES MATRIX - FULL CONTROL MANIFEST */}
              <div className="bg-slate-50 p-5 border border-slate-100 rounded-xl space-y-3">
                <div className="flex justify-between items-center pb-1">
                  <span className="font-bold text-slate-500 uppercase tracking-wider text-[11px]">
                    Structure Matrix Pricing Rules (Accounting-Friendly Formats)
                  </span>
                  {formData.payoutStructure === "progressive" && (
                    <button
                      type="button"
                      onClick={handleAddTier}
                      className="text-blue-600 font-bold hover:text-blue-700 text-xs flex items-center gap-1 transition"
                    >
                      <Plus className="w-3.5 h-3.5" /> Insert Split Boundary
                      Tier
                    </button>
                  )}
                </div>

                <div className="space-y-3 max-h-[38vh] overflow-y-auto pr-1">
                  {formData.brackets.map((bracket, index) => {
                    const isInfinite = bracket.to >= 999999999;
                    return (
                      <div
                        key={index}
                        className="bg-white p-4 border border-slate-200 rounded-xl shadow-sm space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between gap-4"
                      >
                        {formData.payoutStructure !== "progressive" ? (
                          <p className="text-xs text-slate-500 font-medium flex-1">
                            {formData.payoutStructure === "flat_percentage"
                              ? "Applies flat percentage rate uniformly across all total gross revenue volume lines."
                              : "Applies an invariant fixed monetary bounty reward flat fee per closed house transaction."}
                          </p>
                        ) : (
                          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {/* FROM ELEMENT CONTROL */}
                            <div>
                              <span className="block text-[10px] text-slate-400 font-mono font-bold mb-1 uppercase">
                                Tier #{index + 1} Floor Limit:{" "}
                                <span className="text-slate-900 font-sans">
                                  {formatCurrency(bracket.from)}
                                </span>
                              </span>
                              <div className="relative rounded-lg shadow-sm">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-semibold text-xs pointer-events-none">
                                  $
                                </div>
                                <input
                                  type="number"
                                  value={bracket.from === 0 ? "" : bracket.from}
                                  placeholder="0"
                                  onChange={(e) =>
                                    handleBracketChange(
                                      index,
                                      "from",
                                      e.target.value,
                                    )
                                  }
                                  className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-3 py-1.5 text-sm font-mono text-slate-800 focus:outline-none focus:border-slate-400"
                                />
                              </div>
                            </div>

                            {/* TO CEILING CONTROL */}
                            <div>
                              <span className="block text-[10px] text-slate-400 font-mono font-bold mb-1 uppercase">
                                Up To Ceiling Block:{" "}
                                <span className="text-slate-900 font-sans">
                                  {isInfinite
                                    ? "No Limit (∞)"
                                    : formatCurrency(bracket.to)}
                                </span>
                              </span>
                              <div className="flex items-center gap-3">
                                <div className="relative rounded-lg shadow-sm flex-1">
                                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-semibold text-xs pointer-events-none">
                                    $
                                  </div>
                                  <input
                                    type="number"
                                    value={isInfinite ? "" : bracket.to}
                                    placeholder={
                                      isInfinite ? "Unlimited" : "1,000,000"
                                    }
                                    disabled={isInfinite}
                                    onChange={(e) =>
                                      handleBracketChange(
                                        index,
                                        "to",
                                        e.target.value,
                                      )
                                    }
                                    className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-3 py-1.5 text-sm font-mono text-slate-800 focus:outline-none focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
                                  />
                                </div>

                                <label className="flex items-center gap-1.5 cursor-pointer shrink-0 border border-slate-200 bg-slate-50 rounded-lg p-2 hover:bg-slate-100 transition select-none">
                                  <input
                                    type="checkbox"
                                    checked={isInfinite}
                                    onChange={() => toggleInfiniteCap(index)}
                                    className="w-3.5 h-3.5 border-slate-300 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
                                  />
                                  <span className="text-[10px] uppercase font-bold text-slate-500">
                                    No Cap
                                  </span>
                                </label>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* UNIVERSAL RATE VALUE PANEL CONTROLLER */}
                        <div className="flex items-center gap-3 justify-end min-w-[210px] pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
                          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                            Payout Value:
                          </span>
                          <div className="relative rounded-lg shadow-sm w-28">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 pointer-events-none">
                              {formData.payoutStructure === "flat_amount" ? (
                                <DollarSign className="w-4 h-4" />
                              ) : (
                                <Percent className="w-4 h-4" />
                              )}
                            </div>
                            <input
                              type="number"
                              step="0.01"
                              value={bracket.rate}
                              onChange={(e) =>
                                handleBracketChange(
                                  index,
                                  "rate",
                                  e.target.value,
                                )
                              }
                              className="w-full border border-slate-200 bg-white rounded-lg pl-8 pr-3 py-1.5 text-right font-mono font-bold text-slate-900 focus:outline-none text-sm"
                            />
                          </div>
                          {formData.payoutStructure === "progressive" &&
                            formData.brackets.length > 1 && (
                              <button
                                type="button"
                                onClick={() => handleRemoveTier(index)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition shrink-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Modal Footer Controls */}
            <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl sticky bottom-0 z-10">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg font-semibold hover:bg-slate-100 bg-white text-xs transition"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-5 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-slate-300 text-xs shadow-sm transition"
              >
                {submitting ? "Processing..." : "Commit Structure Profile"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ─── DATA TABLE DISPLAY GRID ─── */}
      <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white w-full">
        <table className="w-full text-left border-collapse table-auto">
          <thead>
            <tr className="bg-slate-100 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-bold text-[11px]">
              <th className="py-3.5 px-4 w-32">Salesman ID</th>
              <th className="py-3.5 px-4 w-1/4">Builder Profile Account</th>
              <th className="py-3.5 px-4 w-36">Period Cycle</th>
              <th className="py-3.5 px-4">Commission Rules</th>
              <th className="py-3.5 px-4 w-24 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200/60 text-sm">
            {builders.map((b) => {
              const brackets = b.brackets || [];
              const isFlat =
                brackets.length === 1 && brackets[0].to >= 999999999;
              const isFlatCash = isFlat && brackets[0].isFlatAmount;

              return (
                <tr
                  key={b.salesmanId}
                  className="odd:bg-white even:bg-slate-50/40 hover:bg-slate-100/50 transition-colors"
                >
                  <td className="py-3 px-4 font-mono text-slate-400 font-bold">
                    {b.salesmanId}
                  </td>
                  <td className="py-3 px-4 font-bold text-slate-900">
                    <div className="flex flex-col gap-0.5">
                      <span>{b.name}</span>
                      {b.isManager && (
                        <span className="inline-flex items-center gap-1 w-max text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-blue-600 text-white shadow-sm uppercase tracking-wider">
                          <ShieldAlert className="w-3 h-3" /> Manager (+
                          {b.managerTeamRate || 1.5}%)
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className="uppercase tracking-wider text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200/60 rounded">
                      {formatPeriodLabel(b.periodType)}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-col gap-1.5">
                      {/* Personal Commissions */}
                      <div>
                        {isFlatCash ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-bold text-xs">
                            <DollarSign className="w-3.5 h-3.5" />{" "}
                            {formatCurrency(brackets[0].rate, 2)} Flat Payout
                            Per House Sold
                          </span>
                        ) : isFlat ? (
                          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-bold text-xs">
                            <Percent className="w-3.5 h-3.5" />{" "}
                            {brackets[0].rate}% Flat Rate Payout
                          </span>
                        ) : (
                          <div className="flex flex-wrap gap-1.5 items-center font-mono text-xs text-slate-600">
                            <span className="inline-flex items-center gap-1 mr-1.5 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md font-sans font-bold border border-purple-100 uppercase tracking-wider text-[10px]">
                              <Layers className="w-3 h-3" /> Progressive
                            </span>
                            {brackets.map((tier, tIdx) => (
                              <span
                                key={tIdx}
                                className="bg-white border border-slate-200/80 shadow-sm px-2 py-1 rounded-md text-slate-700 flex items-center gap-1"
                              >
                                <span className="text-slate-900">
                                  {formatCurrency(tier.from)}
                                </span>
                                <span className="text-slate-400 text-[10px]">
                                  &rarr;
                                </span>
                                <span className="text-slate-600">
                                  {tier.to >= 999999999
                                    ? "Unlimited"
                                    : formatCurrency(tier.to)}
                                </span>
                                <span className="text-slate-300 mx-0.5">|</span>
                                <strong className="text-purple-700 font-bold">
                                  {tier.rate}%
                                </strong>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => startEdit(b)}
                        className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 rounded-lg transition"
                        title="Edit config"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(b.salesmanId)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                        title="Delete entry"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UnifiedBuilderManager;

// import React, { useState, useEffect } from "react";
// import {
//   Plus,
//   Trash2,
//   Edit2,
//   X,
//   RefreshCw,
//   AlertCircle,
//   Layers,
//   Percent,
//   DollarSign,
//   ShieldAlert,
// } from "lucide-react";

// const API_URL = "http://10.5.21.42:5000/api/builders";

// const UnifiedBuilderManager = () => {
//   const [builders, setBuilders] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [submitting, setSubmitting] = useState(false);
//   const [error, setError] = useState("");

//   // UI View States
//   const [showForm, setShowForm] = useState(false);
//   const [editingId, setEditingId] = useState(null);

//   const initialFormState = {
//     salesmanId: "",
//     name: "",
//     periodType: "monthly",
//     payoutStructure: "progressive", // "progressive" vs "flat_percentage" vs "flat_amount"
//     brackets: [{ from: 0, to: 1000000, rate: 5.0 }],
//     isManager: false,
//     managerTeamRate: 1.5,
//   };
//   const [formData, setFormData] = useState(initialFormState);

//   // ─── API Transactions ──────────────────────────────────────────────────────
//   const fetchBuilders = async () => {
//     setLoading(true);
//     try {
//       const res = await fetch(API_URL);
//       if (!res.ok)
//         throw new Error("Could not pull registry data from the server.");
//       setBuilders(await res.json());
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchBuilders();
//   }, []);

//   // ─── Event Handlers ────────────────────────────────────────────────────────
//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: type === "checkbox" ? checked : value,
//     }));
//   };

//   const handleStructureChange = (e) => {
//     const type = e.target.value;
//     setFormData((prev) => {
//       let defaultBrackets = [...prev.brackets];

//       if (type === "flat_percentage") {
//         defaultBrackets = [
//           {
//             from: 0,
//             to: 999999999,
//             rate: prev.brackets[0]?.rate || 5.0,
//             isFlatAmount: false,
//           },
//         ];
//       } else if (type === "flat_amount") {
//         defaultBrackets = [
//           {
//             from: 0,
//             to: 999999999,
//             rate: prev.brackets[0]?.rate || 500,
//             isFlatAmount: true,
//           },
//         ];
//       } else if (type === "progressive") {
//         defaultBrackets = [
//           { from: 0, to: 1000000, rate: 4.0, isFlatAmount: false },
//           { from: 1000000, to: 999999999, rate: 6.0, isFlatAmount: false },
//         ];
//       }

//       return { ...prev, payoutStructure: type, brackets: defaultBrackets };
//     });
//   };

//   // ─── UNLOCKED MANUAL BRACKET MANIPULATION ENGINE ────────────────────────────
//   const handleBracketChange = (index, field, val) => {
//     const brackets = [...formData.brackets];

//     // Accept raw text updates, fall back safely to 0 if input clears out entirely
//     const numericValue = val === "" ? 0 : Number(val);
//     brackets[index][field] = numericValue;

//     setFormData((prev) => ({ ...prev, brackets }));
//   };

//   // Toggle infinity cap indicator explicitly per bracket row
//   const toggleInfiniteCap = (index) => {
//     const brackets = [...formData.brackets];
//     if (brackets[index].to >= 999999999) {
//       brackets[index].to = brackets[index].from + 1000000; // Reset to editable range
//     } else {
//       brackets[index].to = 999999999; // Explicit infinity horizon allocation
//     }
//     setFormData((prev) => ({ ...prev, brackets }));
//   };

//   const handleAddTier = () => {
//     const brackets = [...formData.brackets];
//     const last = brackets[brackets.length - 1];

//     // Automatically pre-populate next baseline floor to make user data input flow fast
//     const nextFrom = last ? last.to : 0;
//     const nextTo = nextFrom >= 999999999 ? nextFrom + 1000000 : 999999999;

//     brackets.push({
//       from: nextFrom >= 999999999 ? 0 : nextFrom,
//       to: nextTo,
//       rate: 5.0,
//       isFlatAmount: false,
//     });
//     setFormData((prev) => ({ ...prev, brackets }));
//   };

//   const handleRemoveTier = (index) => {
//     if (formData.brackets.length === 1) return;
//     const brackets = formData.brackets.filter((_, i) => i !== index);
//     setFormData((prev) => ({ ...prev, brackets }));
//   };

//   // ─── CRUD Workflows ────────────────────────────────────────────────────────
//   const startCreate = () => {
//     setEditingId(null);
//     setFormData(initialFormState);
//     setShowForm(true);
//   };

//   const startEdit = (builder) => {
//     const brackets = builder.brackets || [];
//     let structure = "progressive";

//     if (brackets.length === 1 && brackets[0].to >= 999999999) {
//       structure = brackets[0].isFlatAmount ? "flat_amount" : "flat_percentage";
//     }

//     setEditingId(builder.salesmanId);
//     setFormData({
//       salesmanId: builder.salesmanId,
//       name: builder.name,
//       periodType: builder.periodType,
//       payoutStructure: structure,
//       brackets: JSON.parse(JSON.stringify(brackets)),
//       isManager: !!builder.isManager,
//       managerTeamRate:
//         builder.managerTeamRate !== undefined ? builder.managerTeamRate : 1.5,
//     });
//     setShowForm(true);
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Permanently delete this builder?")) return;
//     try {
//       await fetch(`${API_URL}/${id}`, { method: "DELETE" });
//       fetchBuilders();
//     } catch (err) {
//       setError("Failed to delete record.");
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setSubmitting(true);
//     setError("");

//     const { payoutStructure, ...cleanFormData } = formData;
//     const payload = {
//       ...cleanFormData,
//       salesmanId: Number(formData.salesmanId),
//       managerTeamRate: cleanFormData.isManager
//         ? Number(cleanFormData.managerTeamRate)
//         : null,
//       type: "builder",
//     };

//     try {
//       const url = editingId ? `${API_URL}/${editingId}` : API_URL;
//       const res = await fetch(url, {
//         method: editingId ? "PUT" : "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       });
//       if (!res.ok) throw new Error("Server rejected entry parameters.");

//       setShowForm(false);
//       fetchBuilders();
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setSubmitting(false);
//     }
//   };

//   // ─── Pure Accounting Formatting Functions ─────────────────────────
//   const formatCurrency = (val, maxDigits = 0) => {
//     return new Intl.NumberFormat("en-US", {
//       style: "currency",
//       currency: "USD",
//       minimumFractionDigits: 0,
//       maximumFractionDigits: maxDigits,
//     }).format(val || 0);
//   };

//   const formatPeriodLabel = (label) => {
//     if (!label) return "";
//     if (
//       label === "semi_annual" ||
//       label === "semi-annual" ||
//       label === "semi_yearly"
//     ) {
//       return "Semi-Annual";
//     }
//     return label;
//   };

//   if (loading) {
//     return (
//       <div className="flex items-center justify-center min-h-[400px] w-full">
//         <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
//       </div>
//     );
//   }

//   return (
//     <div className="p-8 w-full space-y-6 bg-slate-50/50 text-slate-800 font-sans min-h-screen relative">
//       {/* Header Bar */}
//       <div className="flex items-center justify-between border-b border-slate-200 pb-4 bg-white -mx-8 px-8 pt-2 shadow-sm">
//         <div>
//           <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
//             Builders Commissions Settings
//           </h1>
//         </div>
//         <button
//           onClick={startCreate}
//           className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-slate-800 transition shadow-sm"
//         >
//           <Plus className="w-4 h-4" /> Add Builder
//         </button>
//       </div>

//       {error && (
//         <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2 shadow-sm">
//           <AlertCircle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
//         </div>
//       )}

//       {/* ─── MODAL BOX OVERLAY CONTAINER ─── */}
//       {showForm && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 bg-slate-900/40 backdrop-blur-sm">
//           <form
//             onSubmit={handleSubmit}
//             className="relative bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto space-y-5 flex flex-col transform transition-all"
//           >
//             {/* Modal Header */}
//             <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
//               <h3 className="text-base font-bold text-slate-900 tracking-wide">
//                 {editingId
//                   ? `Modify Configuration Model: ID ${editingId}`
//                   : "Create New Commission Rule Mapping"}
//               </h3>
//               <button
//                 type="button"
//                 onClick={() => setShowForm(false)}
//                 className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition"
//               >
//                 <X className="w-5 h-5" />
//               </button>
//             </div>

//             {/* Modal Form inputs container */}
//             <div className="px-6 space-y-5 flex-1 overflow-y-auto pb-4">
//               <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
//                 <div>
//                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
//                     Salesman ID
//                   </label>
//                   <input
//                     type="number"
//                     name="salesmanId"
//                     disabled={editingId !== null}
//                     value={formData.salesmanId}
//                     onChange={handleInputChange}
//                     required
//                     className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-800 font-mono disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-slate-200 transition"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
//                     Builder Full Name
//                   </label>
//                   <input
//                     type="text"
//                     name="name"
//                     value={formData.name}
//                     onChange={handleInputChange}
//                     required
//                     className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 transition"
//                     placeholder="e.g. Acme Corp"
//                   />
//                 </div>
//                 <div>
//                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
//                     Interval Cycle
//                   </label>
//                   <select
//                     name="periodType"
//                     value={formData.periodType}
//                     onChange={handleInputChange}
//                     className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 transition text-sm"
//                   >
//                     <option value="monthly">Monthly</option>
//                     <option value="quarterly">Quarterly</option>
//                     <option value="semi_annual">Semi-Annual</option>
//                     <option value="yearly">Yearly</option>
//                   </select>
//                 </div>
//                 <div>
//                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
//                     Payout Architecture Type
//                   </label>
//                   <select
//                     name="payoutStructure"
//                     value={formData.payoutStructure}
//                     onChange={handleStructureChange}
//                     className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 font-bold text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
//                   >
//                     <option value="progressive">
//                       Progressive Tier Brackets
//                     </option>
//                     <option value="flat_percentage">
//                       Flat Percentage Rate (%)
//                     </option>
//                     <option value="flat_amount">
//                       Flat Fixed Cash Amount ($)
//                     </option>
//                   </select>
//                 </div>
//               </div>

//               {/* MANAGER SETTINGS SECTION */}
//               <div className="bg-blue-50/40 p-4 border border-blue-100 rounded-xl flex flex-wrap items-center justify-between gap-4">
//                 <label className="flex items-center gap-2.5 cursor-pointer select-none">
//                   <input
//                     type="checkbox"
//                     name="isManager"
//                     checked={formData.isManager}
//                     onChange={handleInputChange}
//                     className="w-4 h-4 border-slate-300 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
//                   />
//                   <div>
//                     <span className="block font-bold text-slate-900 text-xs">
//                       Assign as Sales Manager Role
//                     </span>
//                     <span className="block text-slate-500 text-[11px]">
//                       Enables additional cumulative team-sales tracking
//                       commission cycles.
//                     </span>
//                   </div>
//                 </label>

//                 {formData.isManager && (
//                   <div className="flex items-center gap-2">
//                     <span className="text-xs font-bold text-slate-600 uppercase tracking-wide">
//                       Additional Team Rate:
//                     </span>
//                     <div className="relative rounded-lg shadow-sm w-24">
//                       <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center text-slate-400 pointer-events-none">
//                         <Percent className="w-3.5 h-3.5" />
//                       </div>
//                       <input
//                         type="number"
//                         name="managerTeamRate"
//                         step="0.01"
//                         value={formData.managerTeamRate}
//                         onChange={handleInputChange}
//                         required
//                         className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-2 py-1 text-right font-mono font-bold text-slate-900 focus:outline-none text-xs"
//                       />
//                     </div>
//                   </div>
//                 )}
//               </div>

//               {/* DYNAMIC RULES MATRIX - FULL CONTROL MANIFEST */}
//               <div className="bg-slate-50 p-5 border border-slate-100 rounded-xl space-y-3">
//                 <div className="flex justify-between items-center pb-1">
//                   <span className="font-bold text-slate-500 uppercase tracking-wider text-[11px]">
//                     Structure Matrix Pricing Rules (Accounting-Friendly Formats)
//                   </span>
//                   {formData.payoutStructure === "progressive" && (
//                     <button
//                       type="button"
//                       onClick={handleAddTier}
//                       className="text-blue-600 font-bold hover:text-blue-700 text-xs flex items-center gap-1 transition"
//                     >
//                       <Plus className="w-3.5 h-3.5" /> Insert Split Boundary
//                       Tier
//                     </button>
//                   )}
//                 </div>

//                 <div className="space-y-3 max-h-[38vh] overflow-y-auto pr-1">
//                   {formData.brackets.map((bracket, index) => {
//                     const isInfinite = bracket.to >= 999999999;
//                     return (
//                       <div
//                         key={index}
//                         className="bg-white p-4 border border-slate-200 rounded-xl shadow-sm space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between gap-4"
//                       >
//                         {formData.payoutStructure !== "progressive" ? (
//                           <p className="text-xs text-slate-500 font-medium flex-1">
//                             {formData.payoutStructure === "flat_percentage"
//                               ? "Applies flat percentage rate uniformly across all total gross revenue volume lines."
//                               : "Applies an invariant fixed monetary bounty reward flat fee per closed house transaction."}
//                           </p>
//                         ) : (
//                           <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
//                             {/* FROM ELEMENT CONTROL */}
//                             <div>
//                               <span className="block text-[10px] text-slate-400 font-mono font-bold mb-1 uppercase">
//                                 Tier #{index + 1} Floor Limit:{" "}
//                                 <span className="text-slate-900 font-sans">
//                                   {formatCurrency(bracket.from)}
//                                 </span>
//                               </span>
//                               <div className="relative rounded-lg shadow-sm">
//                                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-semibold text-xs pointer-events-none">
//                                   $
//                                 </div>
//                                 <input
//                                   type="number"
//                                   value={bracket.from === 0 ? "" : bracket.from}
//                                   placeholder="0"
//                                   onChange={(e) =>
//                                     handleBracketChange(
//                                       index,
//                                       "from",
//                                       e.target.value,
//                                     )
//                                   }
//                                   className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-3 py-1.5 text-sm font-mono text-slate-800 focus:outline-none focus:border-slate-400"
//                                 />
//                               </div>
//                             </div>

//                             {/* TO CEILING CONTROL */}
//                             <div>
//                               <span className="block text-[10px] text-slate-400 font-mono font-bold mb-1 uppercase">
//                                 Up To Ceiling Block:{" "}
//                                 <span className="text-slate-900 font-sans">
//                                   {isInfinite
//                                     ? "No Limit (∞)"
//                                     : formatCurrency(bracket.to)}
//                                 </span>
//                               </span>
//                               <div className="flex items-center gap-3">
//                                 <div className="relative rounded-lg shadow-sm flex-1">
//                                   <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-semibold text-xs pointer-events-none">
//                                     $
//                                   </div>
//                                   <input
//                                     type="number"
//                                     value={isInfinite ? "" : bracket.to}
//                                     placeholder={
//                                       isInfinite ? "Unlimited" : "1,000,000"
//                                     }
//                                     disabled={isInfinite}
//                                     onChange={(e) =>
//                                       handleBracketChange(
//                                         index,
//                                         "to",
//                                         e.target.value,
//                                       )
//                                     }
//                                     className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-3 py-1.5 text-sm font-mono text-slate-800 focus:outline-none focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
//                                   />
//                                 </div>

//                                 <label className="flex items-center gap-1.5 cursor-pointer shrink-0 border border-slate-200 bg-slate-50 rounded-lg p-2 hover:bg-slate-100 transition select-none">
//                                   <input
//                                     type="checkbox"
//                                     checked={isInfinite}
//                                     onChange={() => toggleInfiniteCap(index)}
//                                     className="w-3.5 h-3.5 border-slate-300 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
//                                   />
//                                   <span className="text-[10px] uppercase font-bold text-slate-500">
//                                     No Cap
//                                   </span>
//                                 </label>
//                               </div>
//                             </div>
//                           </div>
//                         )}

//                         {/* UNIVERSAL RATE VALUE PANEL CONTROLLER */}
//                         <div className="flex items-center gap-3 justify-end min-w-[210px] pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
//                           <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
//                             Payout Value:
//                           </span>
//                           <div className="relative rounded-lg shadow-sm w-28">
//                             <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 pointer-events-none">
//                               {formData.payoutStructure === "flat_amount" ? (
//                                 <DollarSign className="w-4 h-4" />
//                               ) : (
//                                 <Percent className="w-4 h-4" />
//                               )}
//                             </div>
//                             <input
//                               type="number"
//                               step="0.01"
//                               value={bracket.rate}
//                               onChange={(e) =>
//                                 handleBracketChange(
//                                   index,
//                                   "rate",
//                                   e.target.value,
//                                 )
//                               }
//                               className="w-full border border-slate-200 bg-white rounded-lg pl-8 pr-3 py-1.5 text-right font-mono font-bold text-slate-900 focus:outline-none text-sm"
//                             />
//                           </div>
//                           {formData.payoutStructure === "progressive" &&
//                             formData.brackets.length > 1 && (
//                               <button
//                                 type="button"
//                                 onClick={() => handleRemoveTier(index)}
//                                 className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition shrink-0"
//                               >
//                                 <Trash2 className="w-4 h-4" />
//                               </button>
//                             )}
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>
//             </div>

//             {/* Modal Footer Controls */}
//             <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl sticky bottom-0 z-10">
//               <button
//                 type="button"
//                 onClick={() => setShowForm(false)}
//                 className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg font-semibold hover:bg-slate-100 bg-white text-xs transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 type="submit"
//                 disabled={submitting}
//                 className="px-5 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-slate-300 text-xs shadow-sm transition"
//               >
//                 {submitting ? "Processing..." : "Commit Structure Profile"}
//               </button>
//             </div>
//           </form>
//         </div>
//       )}

//       {/* ─── DATA TABLE DISPLAY GRID ─── */}
//       <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white w-full">
//         <table className="w-full text-left border-collapse table-auto">
//           <thead>
//             <tr className="bg-slate-100 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-bold text-[11px]">
//               <th className="py-3.5 px-4 w-32">Salesman ID</th>
//               <th className="py-3.5 px-4 w-1/4">Builder Profile Account</th>
//               <th className="py-3.5 px-4 w-36">Period Cycle</th>
//               <th className="py-3.5 px-4">Commission Rules</th>
//               <th className="py-3.5 px-4 w-24 text-right">Actions</th>
//             </tr>
//           </thead>
//           <tbody className="divide-y divide-slate-200/60 text-sm">
//             {builders.map((b) => {
//               const brackets = b.brackets || [];
//               const isFlat =
//                 brackets.length === 1 && brackets[0].to >= 999999999;
//               const isFlatCash = isFlat && brackets[0].isFlatAmount;

//               return (
//                 <tr
//                   key={b.salesmanId}
//                   className="odd:bg-white even:bg-slate-50/40 hover:bg-slate-100/50 transition-colors"
//                 >
//                   <td className="py-3 px-4 font-mono text-slate-400 font-bold">
//                     {b.salesmanId}
//                   </td>
//                   <td className="py-3 px-4 font-bold text-slate-900">
//                     <div className="flex flex-col gap-0.5">
//                       <span>{b.name}</span>
//                       {b.isManager && (
//                         <span className="inline-flex items-center gap-1 w-max text-[10px] font-extrabold px-1.5 py-0.5 rounded bg-blue-600 text-white shadow-sm uppercase tracking-wider">
//                           <ShieldAlert className="w-3 h-3" /> Manager (+
//                           {b.managerTeamRate || 1.5}%)
//                         </span>
//                       )}
//                     </div>
//                   </td>
//                   <td className="py-3 px-4">
//                     <span className="uppercase tracking-wider text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200/60 rounded">
//                       {formatPeriodLabel(b.periodType)}
//                     </span>
//                   </td>
//                   <td className="py-3 px-4">
//                     <div className="flex flex-col gap-1.5">
//                       {/* Personal Commissions */}
//                       <div>
//                         {isFlatCash ? (
//                           <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-bold text-xs">
//                             <DollarSign className="w-3.5 h-3.5" />{" "}
//                             {formatCurrency(brackets[0].rate, 2)} Flat Payout
//                             Per House Sold
//                           </span>
//                         ) : isFlat ? (
//                           <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-bold text-xs">
//                             <Percent className="w-3.5 h-3.5" />{" "}
//                             {brackets[0].rate}% Flat Rate Payout
//                           </span>
//                         ) : (
//                           <div className="flex flex-wrap gap-1.5 items-center font-mono text-xs text-slate-600">
//                             <span className="inline-flex items-center gap-1 mr-1.5 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md font-sans font-bold border border-purple-100 uppercase tracking-wider text-[10px]">
//                               <Layers className="w-3 h-3" /> Progressive
//                             </span>
//                             {brackets.map((tier, tIdx) => (
//                               <span
//                                 key={tIdx}
//                                 className="bg-white border border-slate-200/80 shadow-sm px-2 py-1 rounded-md text-slate-700 flex items-center gap-1"
//                               >
//                                 <span className="text-slate-900">
//                                   {formatCurrency(tier.from)}
//                                 </span>
//                                 <span className="text-slate-400 text-[10px]">
//                                   &rarr;
//                                 </span>
//                                 <span className="text-slate-600">
//                                   {tier.to >= 999999999
//                                     ? "Unlimited"
//                                     : formatCurrency(tier.to)}
//                                 </span>
//                                 <span className="text-slate-300 mx-0.5">|</span>
//                                 <strong className="text-purple-700 font-bold">
//                                   {tier.rate}%
//                                 </strong>
//                               </span>
//                             ))}
//                           </div>
//                         )}
//                       </div>
//                     </div>
//                   </td>
//                   <td className="py-3 px-4 text-right">
//                     <div className="inline-flex gap-1">
//                       <button
//                         onClick={() => startEdit(b)}
//                         className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 rounded-lg transition"
//                         title="Edit config"
//                       >
//                         <Edit2 className="w-4 h-4" />
//                       </button>
//                       <button
//                         onClick={() => handleDelete(b.salesmanId)}
//                         className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
//                         title="Delete entry"
//                       >
//                         <Trash2 className="w-4 h-4" />
//                       </button>
//                     </div>
//                   </td>
//                 </tr>
//               );
//             })}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// };

// export default UnifiedBuilderManager;

// // import React, { useState, useEffect } from "react";
// // import {
// //   Plus,
// //   Trash2,
// //   Edit2,
// //   X,
// //   RefreshCw,
// //   AlertCircle,
// //   Layers,
// //   Percent,
// //   DollarSign,
// // } from "lucide-react";

// // const API_URL = "http://10.5.21.42:5000/api/builders";

// // const UnifiedBuilderManager = () => {
// //   const [builders, setBuilders] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [submitting, setSubmitting] = useState(false);
// //   const [error, setError] = useState("");

// //   // UI View States
// //   const [showForm, setShowForm] = useState(false);
// //   const [editingId, setEditingId] = useState(null);

// //   const initialFormState = {
// //     salesmanId: "",
// //     name: "",
// //     periodType: "monthly",
// //     payoutStructure: "progressive", // "progressive" vs "flat_percentage" vs "flat_amount"
// //     brackets: [{ from: 0, to: 1000000, rate: 5.0 }],
// //   };
// //   const [formData, setFormData] = useState(initialFormState);

// //   // ─── API Transactions ──────────────────────────────────────────────────────
// //   const fetchBuilders = async () => {
// //     setLoading(true);
// //     try {
// //       const res = await fetch(API_URL);
// //       if (!res.ok)
// //         throw new Error("Could not pull registry data from the server.");
// //       setBuilders(await res.json());
// //     } catch (err) {
// //       setError(err.message);
// //     } finally {
// //       setLoading(false);
// //     }
// //   };

// //   useEffect(() => {
// //     fetchBuilders();
// //   }, []);

// //   // ─── Event Handlers ────────────────────────────────────────────────────────
// //   const handleInputChange = (e) => {
// //     const { name, value } = e.target;
// //     setFormData((prev) => ({ ...prev, [name]: value }));
// //   };

// //   const handleStructureChange = (e) => {
// //     const type = e.target.value;
// //     setFormData((prev) => {
// //       let defaultBrackets = [...prev.brackets];

// //       if (type === "flat_percentage") {
// //         defaultBrackets = [
// //           {
// //             from: 0,
// //             to: 999999999,
// //             rate: prev.brackets[0]?.rate || 5.0,
// //             isFlatAmount: false,
// //           },
// //         ];
// //       } else if (type === "flat_amount") {
// //         defaultBrackets = [
// //           {
// //             from: 0,
// //             to: 999999999,
// //             rate: prev.brackets[0]?.rate || 500,
// //             isFlatAmount: true,
// //           },
// //         ];
// //       } else if (type === "progressive") {
// //         defaultBrackets = [
// //           { from: 0, to: 1000000, rate: 4.0, isFlatAmount: false },
// //           { from: 1000000, to: 999999999, rate: 6.0, isFlatAmount: false },
// //         ];
// //       }

// //       return { ...prev, payoutStructure: type, brackets: defaultBrackets };
// //     });
// //   };

// //   // ─── UNLOCKED MANUAL BRACKET MANIPULATION ENGINE ────────────────────────────
// //   const handleBracketChange = (index, field, val) => {
// //     const brackets = [...formData.brackets];

// //     // Accept raw text updates, fall back safely to 0 if input clears out entirely
// //     const numericValue = val === "" ? 0 : Number(val);
// //     brackets[index][field] = numericValue;

// //     setFormData((prev) => ({ ...prev, brackets }));
// //   };

// //   // Toggle infinity cap indicator explicitly per bracket row
// //   const toggleInfiniteCap = (index) => {
// //     const brackets = [...formData.brackets];
// //     if (brackets[index].to >= 999999999) {
// //       brackets[index].to = brackets[index].from + 1000000; // Reset to editable range
// //     } else {
// //       brackets[index].to = 999999999; // Explicit infinity horizon allocation
// //     }
// //     setFormData((prev) => ({ ...prev, brackets }));
// //   };

// //   const handleAddTier = () => {
// //     const brackets = [...formData.brackets];
// //     const last = brackets[brackets.length - 1];

// //     // Automatically pre-populate next baseline floor to make user data input flow fast
// //     const nextFrom = last ? last.to : 0;
// //     const nextTo = nextFrom >= 999999999 ? nextFrom + 1000000 : 999999999;

// //     brackets.push({
// //       from: nextFrom >= 999999999 ? 0 : nextFrom,
// //       to: nextTo,
// //       rate: 5.0,
// //       isFlatAmount: false,
// //     });
// //     setFormData((prev) => ({ ...prev, brackets }));
// //   };

// //   const handleRemoveTier = (index) => {
// //     if (formData.brackets.length === 1) return;
// //     const brackets = formData.brackets.filter((_, i) => i !== index);
// //     setFormData((prev) => ({ ...prev, brackets }));
// //   };

// //   // ─── CRUD Workflows ────────────────────────────────────────────────────────
// //   const startCreate = () => {
// //     setEditingId(null);
// //     setFormData(initialFormState);
// //     setShowForm(true);
// //   };

// //   const startEdit = (builder) => {
// //     const brackets = builder.brackets || [];
// //     let structure = "progressive";

// //     if (brackets.length === 1 && brackets[0].to >= 999999999) {
// //       structure = brackets[0].isFlatAmount ? "flat_amount" : "flat_percentage";
// //     }

// //     setEditingId(builder.salesmanId);
// //     setFormData({
// //       salesmanId: builder.salesmanId,
// //       name: builder.name,
// //       periodType: builder.periodType,
// //       payoutStructure: structure,
// //       brackets: JSON.parse(JSON.stringify(brackets)),
// //     });
// //     setShowForm(true);
// //   };

// //   const handleDelete = async (id) => {
// //     if (!window.confirm("Permanently delete this builder?")) return;
// //     try {
// //       await fetch(`${API_URL}/${id}`, { method: "DELETE" });
// //       fetchBuilders();
// //     } catch (err) {
// //       setError("Failed to delete record.");
// //     }
// //   };

// //   const handleSubmit = async (e) => {
// //     e.preventDefault();
// //     setSubmitting(true);
// //     setError("");

// //     const { payoutStructure, ...cleanFormData } = formData;
// //     const payload = {
// //       ...cleanFormData,
// //       salesmanId: Number(formData.salesmanId),
// //       type: "builder",
// //     };

// //     try {
// //       const url = editingId ? `${API_URL}/${editingId}` : API_URL;
// //       const res = await fetch(url, {
// //         method: editingId ? "PUT" : "POST",
// //         headers: { "Content-Type": "application/json" },
// //         body: JSON.stringify(payload),
// //       });
// //       if (!res.ok) throw new Error("Server rejected entry parameters.");

// //       setShowForm(false);
// //       fetchBuilders();
// //     } catch (err) {
// //       setError(err.message);
// //     } finally {
// //       setSubmitting(false);
// //     }
// //   };

// //   // ─── Pure Accounting Formatting Functions ─────────────────────────
// //   const formatCurrency = (val, maxDigits = 0) => {
// //     return new Intl.NumberFormat("en-US", {
// //       style: "currency",
// //       currency: "USD",
// //       minimumFractionDigits: 0,
// //       maximumFractionDigits: maxDigits,
// //     }).format(val || 0);
// //   };

// //   const formatPeriodLabel = (label) => {
// //     if (!label) return "";
// //     if (
// //       label === "semi_annual" ||
// //       label === "semi-annual" ||
// //       label === "semi_yearly"
// //     ) {
// //       return "Semi-Annual";
// //     }
// //     return label;
// //   };

// //   if (loading) {
// //     return (
// //       <div className="flex items-center justify-center min-h-[400px] w-full">
// //         <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
// //       </div>
// //     );
// //   }

// //   return (
// //     <div className="p-8 w-full space-y-6 bg-slate-50/50 text-slate-800 font-sans min-h-screen relative">
// //       {/* Header Bar */}
// //       <div className="flex items-center justify-between border-b border-slate-200 pb-4 bg-white -mx-8 px-8 pt-2 shadow-sm">
// //         <div>
// //           <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">
// //             Builders Commissions Settings
// //           </h1>
// //         </div>
// //         <button
// //           onClick={startCreate}
// //           className="px-4 py-2 bg-slate-900 text-white rounded-lg text-xs font-bold flex items-center gap-1.5 hover:bg-slate-800 transition shadow-sm"
// //         >
// //           <Plus className="w-4 h-4" /> Add Builder
// //         </button>
// //       </div>

// //       {error && (
// //         <div className="p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl flex items-center gap-2 shadow-sm">
// //           <AlertCircle className="w-4 h-4 flex-shrink-0" /> <span>{error}</span>
// //         </div>
// //       )}

// //       {/* ─── MODAL BOX OVERLAY CONTAINER ─── */}
// //       {showForm && (
// //         <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4 bg-slate-900/40 backdrop-blur-sm">
// //           <form
// //             onSubmit={handleSubmit}
// //             className="relative bg-white border border-slate-200 rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-y-auto space-y-5 flex flex-col transform transition-all"
// //           >
// //             {/* Modal Header */}
// //             <div className="flex justify-between items-center border-b border-slate-100 px-6 py-4 sticky top-0 bg-white z-10">
// //               <h3 className="text-base font-bold text-slate-900 tracking-wide">
// //                 {editingId
// //                   ? `Modify Configuration Model: ID ${editingId}`
// //                   : "Create New Commission Rule Mapping"}
// //               </h3>
// //               <button
// //                 type="button"
// //                 onClick={() => setShowForm(false)}
// //                 className="text-slate-400 hover:text-slate-600 p-1.5 hover:bg-slate-50 rounded-lg transition"
// //               >
// //                 <X className="w-5 h-5" />
// //               </button>
// //             </div>

// //             {/* Modal Form inputs container */}
// //             <div className="px-6 space-y-5 flex-1 overflow-y-auto pb-4">
// //               <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
// //                 <div>
// //                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
// //                     Salesman ID
// //                   </label>
// //                   <input
// //                     type="number"
// //                     name="salesmanId"
// //                     disabled={editingId !== null}
// //                     value={formData.salesmanId}
// //                     onChange={handleInputChange}
// //                     required
// //                     className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-slate-50 text-slate-800 font-mono disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-slate-200 transition"
// //                   />
// //                 </div>
// //                 <div>
// //                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
// //                     Builder Full Name
// //                   </label>
// //                   <input
// //                     type="text"
// //                     name="name"
// //                     value={formData.name}
// //                     onChange={handleInputChange}
// //                     required
// //                     className="w-full border border-slate-200 rounded-lg px-3 py-2 bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 transition"
// //                     placeholder="e.g. Acme Corp"
// //                   />
// //                 </div>
// //                 <div>
// //                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
// //                     Interval Cycle
// //                   </label>
// //                   <select
// //                     name="periodType"
// //                     value={formData.periodType}
// //                     onChange={handleInputChange}
// //                     className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-200 transition text-sm"
// //                   >
// //                     <option value="monthly">Monthly</option>
// //                     <option value="quarterly">Quarterly</option>
// //                     <option value="semi_annual">Semi-Annual</option>
// //                     <option value="yearly">Yearly</option>
// //                   </select>
// //                 </div>
// //                 <div>
// //                   <label className="block text-slate-600 font-semibold text-xs mb-1.5">
// //                     Payout Architecture Type
// //                   </label>
// //                   <select
// //                     name="payoutStructure"
// //                     value={formData.payoutStructure}
// //                     onChange={handleStructureChange}
// //                     className="w-full border border-slate-200 bg-white rounded-lg px-3 py-2 font-bold text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-100 transition"
// //                   >
// //                     <option value="progressive">
// //                       Progressive Tier Brackets
// //                     </option>
// //                     <option value="flat_percentage">
// //                       Flat Percentage Rate (%)
// //                     </option>
// //                     <option value="flat_amount">
// //                       Flat Fixed Cash Amount ($)
// //                     </option>
// //                   </select>
// //                 </div>
// //               </div>

// //               {/* DYNAMIC RULES MATRIX - FULL CONTROL MANIFEST */}
// //               <div className="bg-slate-50 p-5 border border-slate-100 rounded-xl space-y-3">
// //                 <div className="flex justify-between items-center pb-1">
// //                   <span className="font-bold text-slate-500 uppercase tracking-wider text-[11px]">
// //                     Structure Matrix Pricing Rules (Accounting-Friendly Formats)
// //                   </span>
// //                   {formData.payoutStructure === "progressive" && (
// //                     <button
// //                       type="button"
// //                       onClick={handleAddTier}
// //                       className="text-blue-600 font-bold hover:text-blue-700 text-xs flex items-center gap-1 transition"
// //                     >
// //                       <Plus className="w-3.5 h-3.5" /> Insert Split Boundary
// //                       Tier
// //                     </button>
// //                   )}
// //                 </div>

// //                 <div className="space-y-3 max-h-[42vh] overflow-y-auto pr-1">
// //                   {formData.brackets.map((bracket, index) => {
// //                     const isInfinite = bracket.to >= 999999999;
// //                     return (
// //                       <div
// //                         key={index}
// //                         className="bg-white p-4 border border-slate-200 rounded-xl shadow-sm space-y-3 sm:space-y-0 sm:flex sm:items-center sm:justify-between gap-4"
// //                       >
// //                         {formData.payoutStructure !== "progressive" ? (
// //                           <p className="text-xs text-slate-500 font-medium flex-1">
// //                             {formData.payoutStructure === "flat_percentage"
// //                               ? "Applies flat percentage rate uniformly across all total gross revenue volume lines."
// //                               : "Applies an invariant fixed monetary bounty reward flat fee per closed house transaction."}
// //                           </p>
// //                         ) : (
// //                           /* COMPLETED REFACTOR: FULL VALUE UNLOCKED CONTROL BLOCK */
// //                           <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
// //                             {/* FROM ELEMENT CONTROL */}
// //                             <div>
// //                               <span className="block text-[10px] text-slate-400 font-mono font-bold mb-1 uppercase">
// //                                 Tier #{index + 1} Floor Limit:{" "}
// //                                 <span className="text-slate-900 font-sans">
// //                                   {formatCurrency(bracket.from)}
// //                                 </span>
// //                               </span>
// //                               <div className="relative rounded-lg shadow-sm">
// //                                 <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-semibold text-xs pointer-events-none">
// //                                   $
// //                                 </div>
// //                                 <input
// //                                   type="number"
// //                                   value={bracket.from === 0 ? "" : bracket.from}
// //                                   placeholder="0"
// //                                   onChange={(e) =>
// //                                     handleBracketChange(
// //                                       index,
// //                                       "from",
// //                                       e.target.value,
// //                                     )
// //                                   }
// //                                   className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-3 py-1.5 text-sm font-mono text-slate-800 focus:outline-none focus:border-slate-400"
// //                                 />
// //                               </div>
// //                             </div>

// //                             {/* TO CEILING CONTROL */}
// //                             <div>
// //                               <span className="block text-[10px] text-slate-400 font-mono font-bold mb-1 uppercase">
// //                                 Up To Ceiling Block:{" "}
// //                                 <span className="text-slate-900 font-sans">
// //                                   {isInfinite
// //                                     ? "No Limit (∞)"
// //                                     : formatCurrency(bracket.to)}
// //                                 </span>
// //                               </span>
// //                               <div className="flex items-center gap-3">
// //                                 <div className="relative rounded-lg shadow-sm flex-1">
// //                                   <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 font-semibold text-xs pointer-events-none">
// //                                     $
// //                                   </div>
// //                                   <input
// //                                     type="number"
// //                                     value={isInfinite ? "" : bracket.to}
// //                                     placeholder={
// //                                       isInfinite ? "Unlimited" : "1,000,000"
// //                                     }
// //                                     disabled={isInfinite}
// //                                     onChange={(e) =>
// //                                       handleBracketChange(
// //                                         index,
// //                                         "to",
// //                                         e.target.value,
// //                                       )
// //                                     }
// //                                     className="w-full border border-slate-200 bg-white rounded-lg pl-7 pr-3 py-1.5 text-sm font-mono text-slate-800 focus:outline-none focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-400"
// //                                   />
// //                                 </div>

// //                                 {/* Explicit Fallback Trigger Toggle button */}
// //                                 <label className="flex items-center gap-1.5 cursor-pointer shrink-0 border border-slate-200 bg-slate-50 rounded-lg p-2 hover:bg-slate-100 transition select-none">
// //                                   <input
// //                                     type="checkbox"
// //                                     checked={isInfinite}
// //                                     onChange={() => toggleInfiniteCap(index)}
// //                                     className="w-3.5 h-3.5 border-slate-300 rounded text-blue-600 focus:ring-blue-500 cursor-pointer"
// //                                   />
// //                                   <span className="text-[10px] uppercase font-bold text-slate-500">
// //                                     No Cap
// //                                   </span>
// //                                 </label>
// //                               </div>
// //                             </div>
// //                           </div>
// //                         )}

// //                         {/* UNIVERSAL RATE VALUE PANEL CONTROLLER */}
// //                         <div className="flex items-center gap-3 justify-end min-w-[210px] pt-2 sm:pt-0 border-t sm:border-t-0 border-slate-100">
// //                           <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
// //                             Payout Value:
// //                           </span>
// //                           <div className="relative rounded-lg shadow-sm w-28">
// //                             <div className="absolute inset-y-0 left-0 pl-3 flex items-center text-slate-400 pointer-events-none">
// //                               {formData.payoutStructure === "flat_amount" ? (
// //                                 <DollarSign className="w-4 h-4" />
// //                               ) : (
// //                                 <Percent className="w-4 h-4" />
// //                               )}
// //                             </div>
// //                             <input
// //                               type="number"
// //                               step="0.01"
// //                               value={bracket.rate}
// //                               onChange={(e) =>
// //                                 handleBracketChange(
// //                                   index,
// //                                   "rate",
// //                                   e.target.value,
// //                                 )
// //                               }
// //                               className="w-full border border-slate-200 bg-white rounded-lg pl-8 pr-3 py-1.5 text-right font-mono font-bold text-slate-900 focus:outline-none text-sm"
// //                             />
// //                           </div>
// //                           {formData.payoutStructure === "progressive" &&
// //                             formData.brackets.length > 1 && (
// //                               <button
// //                                 type="button"
// //                                 onClick={() => handleRemoveTier(index)}
// //                                 className="text-red-500 hover:text-red-700 hover:bg-red-50 p-2 rounded-lg transition shrink-0"
// //                               >
// //                                 <Trash2 className="w-4 h-4" />
// //                               </button>
// //                             )}
// //                         </div>
// //                       </div>
// //                     );
// //                   })}
// //                 </div>
// //               </div>
// //             </div>

// //             {/* Modal Footer Controls */}
// //             <div className="flex justify-end gap-2.5 px-6 py-4 border-t border-slate-100 bg-slate-50 rounded-b-2xl sticky bottom-0 z-10">
// //               <button
// //                 type="button"
// //                 onClick={() => setShowForm(false)}
// //                 className="px-4 py-2 border border-slate-200 text-slate-600 rounded-lg font-semibold hover:bg-slate-100 bg-white text-xs transition"
// //               >
// //                 Cancel
// //               </button>
// //               <button
// //                 type="submit"
// //                 disabled={submitting}
// //                 className="px-5 py-2 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:bg-slate-300 text-xs shadow-sm transition"
// //               >
// //                 {submitting ? "Processing..." : "Commit Structure Profile"}
// //               </button>
// //             </div>
// //           </form>
// //         </div>
// //       )}

// //       {/* ─── DATA TABLE DISPLAY GRID ─── */}
// //       <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white w-full">
// //         <table className="w-full text-left border-collapse table-auto">
// //           <thead>
// //             <tr className="bg-slate-100 border-b border-slate-200 text-slate-500 uppercase tracking-wider font-bold text-[11px]">
// //               <th className="py-3.5 px-4 w-32">Salesman ID</th>
// //               <th className="py-3.5 px-4 w-1/5">Builder Profile Account</th>
// //               <th className="py-3.5 px-4 w-36">Period Cycle</th>
// //               <th className="py-3.5 px-4">Commission Rules</th>
// //               <th className="py-3.5 px-4 w-24 text-right">Actions</th>
// //             </tr>
// //           </thead>
// //           <tbody className="divide-y divide-slate-200/60 text-sm">
// //             {builders.map((b) => {
// //               const brackets = b.brackets || [];
// //               const isFlat =
// //                 brackets.length === 1 && brackets[0].to >= 999999999;
// //               const isFlatCash = isFlat && brackets[0].isFlatAmount;

// //               return (
// //                 <tr
// //                   key={b.salesmanId}
// //                   className="odd:bg-white even:bg-slate-50/40 hover:bg-slate-100/50 transition-colors"
// //                 >
// //                   <td className="py-3 px-4 font-mono text-slate-400 font-bold">
// //                     {b.salesmanId}
// //                   </td>
// //                   <td className="py-3 px-4 font-bold text-slate-900">
// //                     {b.name}
// //                   </td>
// //                   <td className="py-3 px-4">
// //                     <span className="uppercase tracking-wider text-[10px] font-bold px-2 py-0.5 bg-slate-100 text-slate-600 border border-slate-200/60 rounded">
// //                       {formatPeriodLabel(b.periodType)}
// //                     </span>
// //                   </td>
// //                   <td className="py-3 px-4">
// //                     {isFlatCash ? (
// //                       <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 font-bold text-xs">
// //                         <DollarSign className="w-3.5 h-3.5" />{" "}
// //                         {formatCurrency(brackets[0].rate, 2)} Flat Payout Per
// //                         House Sold
// //                       </span>
// //                     ) : isFlat ? (
// //                       <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100 font-bold text-xs">
// //                         <Percent className="w-3.5 h-3.5" /> {brackets[0].rate}%
// //                         Flat Rate Payout
// //                       </span>
// //                     ) : (
// //                       <div className="flex flex-wrap gap-1.5 items-center font-mono text-xs text-slate-600">
// //                         <span className="inline-flex items-center gap-1 mr-1.5 px-2 py-0.5 bg-purple-50 text-purple-700 rounded-md font-sans font-bold border border-purple-100 uppercase tracking-wider text-[10px]">
// //                           <Layers className="w-3 h-3" /> Progressive
// //                         </span>
// //                         {brackets.map((tier, tIdx) => (
// //                           <span
// //                             key={tIdx}
// //                             className="bg-white border border-slate-200/80 shadow-sm px-2 py-1 rounded-md text-slate-700 flex items-center gap-1"
// //                           >
// //                             <span className="text-slate-900">
// //                               {formatCurrency(tier.from)}
// //                             </span>
// //                             <span className="text-slate-400 text-[10px]">
// //                               &rarr;
// //                             </span>
// //                             <span className="text-slate-600">
// //                               {tier.to >= 999999999
// //                                 ? "Unlimited"
// //                                 : formatCurrency(tier.to)}
// //                             </span>
// //                             <span className="text-slate-300 mx-0.5">|</span>
// //                             <strong className="text-purple-700 font-bold">
// //                               {tier.rate}%
// //                             </strong>
// //                           </span>
// //                         ))}
// //                       </div>
// //                     )}
// //                   </td>
// //                   <td className="py-3 px-4 text-right">
// //                     <div className="inline-flex gap-1">
// //                       <button
// //                         onClick={() => startEdit(b)}
// //                         className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-200/50 rounded-lg transition"
// //                         title="Edit config"
// //                       >
// //                         <Edit2 className="w-4 h-4" />
// //                       </button>
// //                       <button
// //                         onClick={() => handleDelete(b.salesmanId)}
// //                         className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
// //                         title="Delete entry"
// //                       >
// //                         <Trash2 className="w-4 h-4" />
// //                       </button>
// //                     </div>
// //                   </td>
// //                 </tr>
// //               );
// //             })}
// //           </tbody>
// //         </table>
// //       </div>
// //     </div>
// //   );
// // };

// // export default UnifiedBuilderManager;
